import { Injectable } from '@angular/core';
import { IamOrganizationsApiService } from '@kempoweroyj/ngx-iam-organizations-api-client';
import { IamUsersApiService, UserTenancyAccessObject } from '@kempoweroyj/ngx-iam-users-api-client';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CustomerPrivacyService } from 'layout/customer-privacy/customer-privacy.service';
import { AuthService } from 'providers/auth.service';
import { LocalStoreService } from 'providers/local-store.service';
import {
    BehaviorSubject,
    Observable,
    catchError,
    distinctUntilChanged,
    filter,
    from,
    map,
    of,
    skip,
    switchMap,
    take,
} from 'rxjs';
import { StationsService } from 'shared/stations-service/stations.service';
import { LicenseInfoService } from './license-info.service';

const LOCAL_STORAGE_PATH = 'chargeye-organization-info';

export interface OrganizationInfo {
    originalTenantId: string;
    originalTenantName: string;
    impersonated?: {
        tenantId: string;
        tenantName: string;
    };
}

const localStore = new LocalStoreService<OrganizationInfo>(LOCAL_STORAGE_PATH);

// Set initial value
let initialValue = null;
try {
    initialValue = localStore.get() || false;
} catch (e) {
    console.error(e);
}
const _userTenancyAccess$ = new BehaviorSubject<UserTenancyAccessObject[]>(null);
const _organizationInfo$ = new BehaviorSubject<OrganizationInfo>(initialValue);

/**
 * Set local storage when organization info changes
 */
_organizationInfo$.subscribe((info) => {
    localStore.set(info);
});

@Injectable({
    providedIn: 'root',
})
@UntilDestroy()
/**
 * This Service is used to store organization values (id, name) when impersonating to a other organization
 */
export class OrganizationInfoService {
    /**
     * Getter: Organization name with customer privacy applied
     */
    get organizationName(): string {
        const tenant = this.organizationInfo
            ? this.organizationInfo.impersonated?.tenantName || this.organizationInfo.originalTenantName
            : null;

        if (!tenant) {
            return null;
        }

        return this.customerPrivacyService.transform(tenant, 'org');
    }

    /**
     * Getter: Organization Info snapshot with customer privacy applied
     */
    get organizationInfo(): OrganizationInfo {
        return _organizationInfo$.value;
    }

    /**
     * Getter: Organization Info Observable with customer privacy applied
     */
    get organizationInfo$(): Observable<OrganizationInfo> {
        return _organizationInfo$.asObservable();
    }
    get userTenancyAccess(): UserTenancyAccessObject[] {
        return _userTenancyAccess$.value;
    }
    get userTenancyAccess$(): Observable<UserTenancyAccessObject[]> {
        return _userTenancyAccess$.asObservable();
    }

    constructor(
        private organizationApi: IamOrganizationsApiService,
        private iamUsersApi: IamUsersApiService,
        private auth: AuthService,
        private customerPrivacyService: CustomerPrivacyService,
        private readonly stationsService: StationsService,
        private readonly licenseInfoService: LicenseInfoService
    ) {
        this.auth.authenticated$.subscribe({
            complete: () => {
                // Clear organization info when user logs out
                _organizationInfo$.next(null);
                _userTenancyAccess$.next(null);
            },
        });

        // fetch user tenancies when user logs in and is not impersonated
        // store tenancies in userTenancyAccess$ subject
        this.auth.authenticated$
            .pipe(
                filter((isAuthenticated) => isAuthenticated),
                switchMap(() => {
                    if (!this.auth.profile.impersonated) {
                        return from(this.requestMyTenancyAccess$());
                    }
                    return of(null);
                })
            )
            .subscribe({
                next: (userTenancyAccess) => {
                    if (userTenancyAccess) {
                        _userTenancyAccess$.next(userTenancyAccess);
                    }
                },
            });

        // fetch organization info when user logs in and organizationInfo is not set (e.g. after page refresh)
        this.auth.authenticated$
            .pipe(
                filter((isAuthenticated) => {
                    // Only proceed when authenticated AND profile is loaded
                    const hasProfile = !!this.auth.profile;
                    return isAuthenticated && hasProfile;
                }),
                switchMap(() => {
                    // if organizationInfo is already set, return it
                    if (this.organizationInfo) {
                        return of(this.organizationInfo);
                    }
                    // otherwise fetch organization info
                    return this.requestOrganizationInfo$();
                })
            )
            .subscribe({
                next: (orgInfo) => {
                    if (orgInfo) {
                        _organizationInfo$.next(orgInfo);
                    }
                },
            });

        // Trigger organizationInfo$ update when customer privacy is enabled/disabled
        this.customerPrivacyService.enabled$
            .pipe(
                distinctUntilChanged(),
                // Only proceed if authenticated and profile is loaded
                filter(() => !!this.auth.profile),
                switchMap((enabled) => {
                    if (enabled) {
                        // mask
                        const masked = this.maskOrganizationInfo(this.organizationInfo);

                        // return
                        return of(masked);
                    } else {
                        // get back original value
                        return this.requestOrganizationInfo$();
                    }
                }),
                untilDestroyed(this)
            )
            .subscribe((info) => {
                // set local storage
                _organizationInfo$.next(info);
            });

        _organizationInfo$
            .pipe(
                skip(1),
                filter((info) => !!info),
                untilDestroyed(this)
            )
            .subscribe(() => {
                this.stationsService.refreshCachedStations();
            });
    }

    setImpersonation(tenantId: string, tenantName: string): void {
        if (!this.organizationInfo) {
            return;
        }

        // mask tenantName
        const masked = this.customerPrivacyService.transform(tenantName, 'org');

        const impersonated = {
            tenantId,
            tenantName: masked,
        };

        // set local storage
        _organizationInfo$.next({
            ...this.organizationInfo,
            impersonated,
        });

        // Refresh license information for the impersonated organization
        // Note: This is async but we don't await it here to avoid blocking the UI
        // The license will be updated in the background
        this.licenseInfoService.refreshLicense();
    }

    removeImpersonation(): void {
        if (!this.organizationInfo) {
            return;
        }

        // Fetch fresh organization info from the API to ensure we have the correct
        // organization name after returning from impersonation
        // Using take(1) to auto-complete the subscription after first emission
        this.requestOrganizationInfo$()
            .pipe(take(1))
            .subscribe((orgInfo) => {
                if (orgInfo) {
                    _organizationInfo$.next(orgInfo);
                }
            });

        // Refresh license information back to the original organization
        // Note: This is async but we don't await it here to avoid blocking the UI
        // The license will be updated in the background
        this.licenseInfoService.refreshLicense();
    }

    requestMyTenancyAccess$() {
        return from(this.iamUsersApi.getMyTenancyAccess()).pipe(
            catchError((err) => {
                console.error(`iamUsersApi#getMyTenancyAccess ${JSON.stringify(err, null, 2)}`);
                return of(null);
            })
        );
    }

    requestOrganizationInfo$() {
        return from(this.organizationApi.getMyOrganization({ minimized: true })).pipe(
            catchError((err) => {
                console.error(`organizationApi#getMyOrganization ${JSON.stringify(err, null, 2)}`);
                return of(null);
            }),
            map((res) => {
                if (!res) {
                    return null;
                }
                return { originalTenantId: res.tenantId, originalTenantName: res.name };
            })
        );
    }

    /**
     * Helper: apply customer privacy to organization info
     */
    maskOrganizationInfo(info: OrganizationInfo): OrganizationInfo {
        if (!info) {
            return null;
        }

        let impersonated = info.impersonated || undefined;
        if (impersonated) {
            impersonated = {
                ...impersonated,
                tenantName: this.customerPrivacyService.transform(impersonated.tenantName, 'org'),
            };
        }

        let originalTenantName = info.originalTenantName || undefined;
        if (originalTenantName) {
            originalTenantName = this.customerPrivacyService.transform(originalTenantName, 'org');
        }

        return {
            ...info,
            originalTenantName,
            impersonated,
        };
    }
}
